import "./styles.scss";

import { useRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { Formik, Form } from "formik";
import queryString from "query-string";

import { useApp } from "context/app";
import { useNewTask } from "context/client/new_task";

import DashboardContentLayout from "components/layouts/DashboardContentLayout";
import Input from "components/form/Input";
import FormikSelect from "components/formik/FormikSelect";
import FormikInput from "components/formik/FormikInput";
import FormikSelectablePill from "components/formik/FormikSelectablePill";
import TaskInfo from "components/TaskInfo";
import FormikTextEditor from "components/formik/FormikTextEditor";
import FormikFilesUpload from "components/formik/FormikFilesUpload";
import Button from "components/Button";
import {
  StatusExplanation,
  PriorityExplanation,
} from "page_components/shared/tasks/explanations";
import TaskOnboarding from "components/TaskOnboarding";
import ScrollTopMobile from "components/ScrollTopMobile";

import useResponsive from "libs/helpers/useResponsive";
import newTaskFormSchema from "libs/validations/new_task_form";

import task_status_data from "libs/task_status_data.json";
import task_priority_data from "libs/task_priority_data.json";

const mainClass = "new-task-content";

const NewTaskContent = () => {
  const { t } = useTranslation();
  const { search, pathname } = useLocation();
  const query = queryString.parse(search);
  const navigate = useNavigate();
  const { max } = useResponsive();

  const { current_website, websites } = useApp();
  const { file_progress, submitForm } = useNewTask();

  const [explanation, setExplanation] = useState(null);
  const [task_onboarding, setTaskOnboarding] = useState(false);

  const [initialFormValues, setInitialFormValues] = useState({
    website: current_website?._id,
    title: "",
    priority: 2,
    status: 1,
    description: "",
    files: [],
  });

  const formValues = useRef(initialFormValues);

  useEffect(() => {
    setInitialFormValues({
      ...formValues.current,
      website: current_website?._id,
    });
  }, [current_website]);

  const onSubmit = async (values, setSubmitting) => {
    try {
      await submitForm(values);
    } catch (err) {
      setSubmitting(false);
    }
  };

  const actions = [
    {
      label: t("Add new website"),
      href: "/websites/new",
      event: () =>
        window.gtag("event", "new_website", {
          click_text: t("Add new website"),
          click_url: `/websites/new`,
        }),
    },
  ];

  useEffect(() => {
    if (query?.onboarding === "new") {
      setTaskOnboarding(true);
    }
    // eslint-disable-next-line
  }, [query?.onboarding]);

  useEffect(() => {
    if (task_onboarding && max.sm) {
      const layoutBody = document.querySelector(
        ".dashboard-layout-content__body",
      );

      setTaskOnboarding(null);

      if (layoutBody) layoutBody.style.overflow = "";

      navigate("/tasks/new", { replace: true });
    }
  }, [task_onboarding, navigate, max.sm]);

  const onOnboardingClose = () => {
    const referrer = query?.referrer;

    window.history.replaceState({}, null, pathname);

    setTaskOnboarding(false);

    if (!!referrer) navigate(referrer);
  };

  return (
    <>
      <DashboardContentLayout
        header={{
          label: t("Create new task"),
          description: t("Add a new task to be completed"),
        }}
        actions={actions}
      >
        <Formik
          initialValues={initialFormValues}
          enableReinitialize
          validationSchema={newTaskFormSchema}
          onSubmit={(values, { setSubmitting }) => {
            onSubmit(values, setSubmitting);
          }}
        >
          {({ values, errors, setFieldTouched, isSubmitting }) => {
            formValues.current = values;

            return (
              <>
                <Form
                  className={mainClass}
                  autoComplete="off"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && e.target.tagName !== "TEXTAREA") {
                      e.preventDefault();
                      return false;
                    }
                  }}
                >
                  <div className={`${mainClass}__column`}>
                    <div id="website_select" className={`${mainClass}__field`}>
                      {websites?.length > 1 ? (
                        <FormikSelect
                          name="website"
                          label={t("Website")}
                          options={websites.map((website) => ({
                            label: website.website,
                            value: website._id,
                          }))}
                        />
                      ) : (
                        <Input
                          name="website"
                          label={t("Website")}
                          value={current_website?.website}
                          readOnly
                        />
                      )}
                    </div>
                    <div id="website_title" className={`${mainClass}__field`}>
                      <FormikInput
                        name="title"
                        label={t("Title")}
                        disabled={isSubmitting}
                        placeholder={t("Add task title")}
                      />
                    </div>
                    {max.sm ? (
                      <>
                        <div className={`${mainClass}__field`}>
                          {(() => {
                            const priority = values?.priority;
                            const selected_option = task_priority_data.find(
                              (item) => item.value === priority,
                            );

                            return (
                              <FormikSelect.Priority
                                labelCta={{
                                  text: t("Learn more"),
                                  onClick: () => setExplanation("priority"),
                                }}
                                name="priority"
                                selected={{
                                  value: priority,
                                  label: t(selected_option?.label),
                                  color: selected_option?.color,
                                }}
                              />
                            );
                          })()}
                        </div>
                        <div className={`${mainClass}__field`}>
                          {(() => {
                            const status = values?.status;
                            const selected_option = task_status_data.find(
                              (item) => item.value === status,
                            );

                            return (
                              <FormikSelect.Status
                                labelCta={{
                                  text: t("Learn more"),
                                  onClick: () => setExplanation("status"),
                                }}
                                name="status"
                                selected={{
                                  value: status,
                                  label: t(selected_option?.label),
                                  color: selected_option?.color,
                                }}
                                optionFilters={["canceled"]}
                                optionsDisabled={[
                                  task_status_data.find(
                                    (item) => item.key === "closed",
                                  ).value,
                                ]}
                              />
                            );
                          })()}
                        </div>
                      </>
                    ) : (
                      <div
                        className={`${mainClass}__field ${mainClass}__field--row`}
                      >
                        <div id="website_priority" className="priority">
                          <p className={`${mainClass}__field-label`}>
                            {t("Priority")}
                          </p>
                          {(() => {
                            const priority = values?.priority;
                            const selected_option = task_priority_data.find(
                              (item) => item.value === priority,
                            );

                            return (
                              <FormikSelectablePill
                                name="priority"
                                data={{
                                  value: priority,
                                  label: t(selected_option?.label),
                                  color: selected_option?.color,
                                }}
                                icon="flag"
                                options={task_priority_data.map((item) => ({
                                  ...item,
                                  label: t(item.label),
                                }))}
                                optionsFooter={
                                  <TaskInfo
                                    type="priority"
                                    onClick={() => setExplanation("priority")}
                                  />
                                }
                                isDisabled={isSubmitting}
                              />
                            );
                          })()}
                        </div>
                        <div id="website_status" className="status">
                          <p className={`${mainClass}__field-label`}>Status</p>
                          {(() => {
                            const status = values?.status;
                            const selected_option = task_status_data.find(
                              (item) => item.value === status,
                            );

                            return (
                              <FormikSelectablePill
                                name="status"
                                data={{
                                  value: status,
                                  label: t(selected_option?.label),
                                  color: selected_option?.color,
                                }}
                                options={task_status_data
                                  .filter((item) => item?.key !== "canceled")
                                  .map((item) => ({
                                    ...item,
                                    label: t(item.label),
                                  }))}
                                optionsDisabled={[
                                  task_status_data.find(
                                    (item) => item.key === "closed",
                                  ).value,
                                ]}
                                optionsFooter={
                                  <TaskInfo
                                    onClick={() => setExplanation("status")}
                                  />
                                }
                                isDisabled={isSubmitting}
                              />
                            );
                          })()}
                        </div>
                      </div>
                    )}
                    <div id="website_files" className={`${mainClass}__field`}>
                      <FormikFilesUpload
                        name="files"
                        label={t("Attachments")}
                        progress={file_progress}
                        inputId="new-task-files"
                      />
                    </div>
                  </div>
                  <div className={`${mainClass}__column`}>
                    <div
                      id="website_description"
                      className={`${mainClass}__field ${mainClass}__field--description`}
                    >
                      <p className={`${mainClass}__field-label`}>
                        {t("Description")}
                      </p>
                      <FormikTextEditor
                        id="description"
                        name="description"
                        placeholder={t(
                          "Add as much information as possible so we can process your request faster.",
                        )}
                        attachmentInputId="new-task-files"
                        isDisabled={isSubmitting}
                      />
                    </div>
                    <div className={`${mainClass}__submit`}>
                      <Button
                        id="website_submit"
                        type="submit"
                        loading={!!isSubmitting}
                      >
                        {t("Create task")}
                      </Button>
                    </div>
                  </div>
                </Form>

                {!!task_onboarding && (
                  <TaskOnboarding
                    init_step={1}
                    onClose={onOnboardingClose}
                    validate={setFieldTouched}
                    setExplanation={setExplanation}
                    errors={errors}
                  />
                )}
              </>
            );
          }}
        </Formik>

        {max.sm && (
          <div className={`${mainClass}__scroll`}>
            <ScrollTopMobile />
          </div>
        )}
      </DashboardContentLayout>

      <StatusExplanation
        isOpen={explanation === "status"}
        onClose={() => setExplanation(null)}
      />
      <PriorityExplanation
        isOpen={explanation === "priority"}
        onClose={() => setExplanation(null)}
      />
    </>
  );
};

export default NewTaskContent;
